import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useApi, Spinner, ErrBanner, today, fmtNum } from '../../hooks/useApi';
import { api } from '../../services/api';
import ConfirmModal from '../../components/ConfirmModal';
import { useAuth } from '../../context/AuthContext';
import './ItemLoss.css';

const LOSS_TYPES = ['Evaporation', 'Spillage', 'Testing', 'Leakage', 'Other'];
const EMPTY = { loss_date: today(), product_code: 'HSD', loss_type: 'Evaporation', qty: '', rate: '' };

export default function ItemLoss() {
  const { t: tr } = useTranslation();
  const { can, canAccess } = useAuth();
  const isAdmin = can('item_loss', 'approve');
  const { data: products } = useApi(() => api.products.list(), []);
  const { data: records, loading, error, reload } = useApi(() => api.itemLoss.list(), []);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editRow, setEditRow] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');
  const [confirm, setConfirm] = useState(null);
  const notify = (m) => { setMsg(m); setTimeout(() => setMsg(''), 3500); };
  const isToday = (d) => d === today();

  const handleSave = async () => {
    if (!form.qty || !form.rate) { notify('❌ ' + tr('itemLoss.errors.qtyRateRequired')); return; }
    setSaving(true);
    try {
      await api.itemLoss.create({ ...form, qty: parseFloat(form.qty), rate: parseFloat(form.rate) });
      notify('✅ ' + tr('itemLoss.toasts.saved')); setShowForm(false); setForm(EMPTY); reload();
    } catch (e) { notify('❌ ' + e.message); }
    setSaving(false);
  };

  const handleEditSave = async () => {
    setSaving(true);
    try {
      await api.itemLoss.update(editRow.id, { product_code: editRow.product_code, loss_type: editRow.loss_type, qty: parseFloat(editRow.qty), rate: parseFloat(editRow.rate) });
      notify('✅ ' + tr('common.updatedSuccessfully')); setEditRow(null); reload();
    } catch (e) { notify('❌ ' + e.message); }
    setSaving(false);
  };

  const handleDelete = async () => {
    try { await api.itemLoss.remove(confirm.row.id); notify('✅ ' + tr('common.deletedSuccessfully')); reload(); }
    catch (e) { notify('❌ ' + e.message); }
    setConfirm(null);
  };

  const total = (records || []).reduce((s, r) => s + Number(r.amount || 0), 0);
  if (!canAccess('item_loss')) {
    return (
      <div className="item-loss-page">
        <ErrBanner msg={tr('itemLoss.accessDenied')} />
      </div>
    );
  }
  return (
    <div className="item-loss-page">
      <div className="page-header">
        <h2 className="page-title">{tr('itemLoss.title')}</h2>
        {can('item_loss', 'create') && (
          <button className="btn btn-primary btn-sm" onClick={() => setShowForm(!showForm)}>
            ＋ {tr('itemLoss.addEntry')}
          </button>
        )}      </div>
      {msg && <div style={{ padding: '6px 12px', background: '#fff5f5', border: '1px solid #feb2b2', borderRadius: 6, marginBottom: 8, fontSize: 13 }}>{msg}</div>}

      {showForm && (
        <div className="section-card">
          <div className="section-header">{tr('itemLoss.newEntry')}</div>
          <div className="form-row">
            <div className="form-group"><label>{tr('common.date')}</label>
              <input type="date" className="form-control" value={form.loss_date} onChange={e => setForm({ ...form, loss_date: e.target.value })} />
            </div>
            <div className="form-group"><label>{tr('common.product')}</label>
              <select className="form-control" value={form.product_code} onChange={e => setForm({ ...form, product_code: e.target.value })}>
                {(products || []).map(p => <option key={p.id} value={p.code}>{p.name}</option>)}
              </select>
            </div>
            <div className="form-group"><label>{tr('itemLoss.lossType')}</label>
              <select className="form-control" value={form.loss_type} onChange={e => setForm({ ...form, loss_type: e.target.value })}>
                {LOSS_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div className="form-group"><label>{tr('itemLoss.qtyLtr')}</label>
              <input type="number" className="form-control" value={form.qty} onChange={e => setForm({ ...form, qty: e.target.value })} />
            </div>
            <div className="form-group"><label>{tr('itemLoss.rateRs')}</label>
              <input type="number" className="form-control" value={form.rate} onChange={e => setForm({ ...form, rate: e.target.value })} />
            </div>
            {form.qty && form.rate && (
              <div className="form-group balance-group"><label>{tr('common.amount')}</label>
                <div className="balance-value">₹{fmtNum(parseFloat(form.qty) * parseFloat(form.rate))}</div>
              </div>
            )}
          </div>
          <div className="action-bar" style={{ padding: '0 16px 14px' }}>
            <button className="btn btn-primary" onClick={handleSave} disabled={saving}>{saving ? tr('common.saving') + '…' : '💾 ' + tr('common.save')}</button>
            <button className="btn btn-secondary" onClick={() => setShowForm(false)}>✕ {tr('common.cancel')}</button>
          </div>
        </div>
      )}

      {editRow && (
        <div className="section-card" style={{ borderLeft: '4px solid var(--primary)' }}>
          <div className="section-header">✏️ {tr('itemLoss.editEntry')}</div>
          <div className="form-row">
            <div className="form-group"><label>{tr('common.product')}</label>
              <select className="form-control" value={editRow.product_code} onChange={e => setEditRow({ ...editRow, product_code: e.target.value })}>
                {(products || []).map(p => <option key={p.id} value={p.code}>{p.name}</option>)}
              </select>
            </div>
            <div className="form-group"><label>{tr('itemLoss.lossType')}</label>
              <select className="form-control" value={editRow.loss_type} onChange={e => setEditRow({ ...editRow, loss_type: e.target.value })}>
                {LOSS_TYPES.map(t => <option key={t}>{t}</option>)}
              </select>
            </div>
            <div className="form-group"><label>{tr('itemLoss.qtyLtr')}</label>
              <input type="number" className="form-control" value={editRow.qty} onChange={e => setEditRow({ ...editRow, qty: e.target.value })} />
            </div>
            <div className="form-group"><label>{tr('itemLoss.rateRs')}</label>
              <input type="number" className="form-control" value={editRow.rate} onChange={e => setEditRow({ ...editRow, rate: e.target.value })} />
            </div>
          </div>
          <div className="action-bar" style={{ padding: '0 16px 14px' }}>
            <button className="btn btn-primary" onClick={handleEditSave} disabled={saving}>{saving ? tr('common.saving') + '…' : '💾 ' + tr('common.update')}</button>
            <button className="btn btn-secondary" onClick={() => setEditRow(null)}>✕ {tr('common.cancel')}</button>
          </div>
        </div>
      )}

      {loading && <Spinner />}
      {error && <ErrBanner msg={error} />}

      {!loading && (
        <div className="section-card">
          <div className="section-header">{tr('itemLoss.recordsTotal', { total: fmtNum(total) })}</div>
          <div className="table-wrapper">
            <table className="data-table">
              <thead><tr>
                <th>#</th><th style={{ textAlign: 'left' }}>{tr('common.date')}</th><th style={{ textAlign: 'left' }}>{tr('common.product')}</th>
                <th style={{ textAlign: 'left' }}>{tr('itemLoss.lossType')}</th><th>{tr('itemLoss.qtyLtr')}</th>
                <th>{tr('itemLoss.rateRs')}</th><th>{tr('itemLoss.amountRs')}</th><th style={{ textAlign: 'center' }}>{tr('common.actions')}</th>
              </tr></thead>
              <tbody>
                {(records || []).map((r, i) => (
                  <tr key={r.id}>
                    <td>{i + 1}</td><td>{r.loss_date}</td>
                    <td><span className="badge badge-info">{r.product_code}</span></td>
                    <td>{r.loss_type}</td>
                    <td className="right-align">{r.qty}</td>
                    <td className="right-align">₹{fmtNum(r.rate)}</td>
                    <td className="right-align">₹{fmtNum(r.amount)}</td>
                    <td style={{ textAlign: 'center', whiteSpace: 'nowrap' }}>
                      {can('item_loss', 'update') && (isAdmin || isToday(r.loss_date)) && (
                        <button title={tr('common.edit')} style={btnEdit} onClick={() => setEditRow({ ...r })}>✏️</button>
                      )}
                      {can('item_loss', 'delete') && (

                        <button title={tr('common.delete')} style={btnDel} onClick={() => setConfirm({ row: r })}>🗑️</button>)}
                    </td>
                  </tr>
                ))}
                {(records || []).length === 0 && (
                  <tr><td colSpan={8} style={{ textAlign: 'center', padding: 20, color: 'var(--text-muted)' }}>{tr('itemLoss.noRecords')}</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <ConfirmModal
        isOpen={!!confirm}
        title={tr('itemLoss.deleteTitle')}
        message={tr('itemLoss.deleteMessage')}
        onConfirm={handleDelete}
        onCancel={() => setConfirm(null)}
      />
    </div>
  );
}

const btnEdit = { background: '#ebf8ff', color: '#2b6cb0', border: '1px solid #bee3f8', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', marginRight: 4, fontSize: 13 };
const btnDel = { background: '#fff5f5', color: '#c53030', border: '1px solid #feb2b2', borderRadius: 6, padding: '3px 8px', cursor: 'pointer', fontSize: 13 };
